import React, { useState, useMemo, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  TextField,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  Typography,
  Collapse,
  makeStyles,
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Description as ScenarioIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Inbox as UngroupedIcon,
  Visibility as SharedIcon,
  VisibilityOff as PrivateIcon,
} from "@material-ui/icons";
import { Tooltip, IconButton } from "@material-ui/core";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  selectorRoot: {
    position: "relative",
  },
  selectorButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 0 7px",
    cursor: "pointer",
    minWidth: 200,
    backgroundColor: "transparent",
    borderBottom: "1px solid #9e9e9e",
    transition: "border-color 200ms",
    "&:hover": {
      borderBottomColor: "#616161",
    },
  },
  selectorButtonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
    "&:hover": {
      borderBottomColor: "#9e9e9e",
    },
  },
  selectorButtonFocused: {
    borderBottomWidth: "2px",
    borderBottomColor: (props) =>
      props.clientColor || theme.palette.primary.main,
    paddingBottom: "6px",
  },
  placeholder: {
    color: theme.palette.text.secondary,
    fontStyle: "italic",
  },
  selectedText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
  },
  expandIcon: {
    color: theme.palette.text.secondary,
    transition: "transform 0.2s",
  },
  expandIconOpen: {
    transform: "rotate(180deg)",
  },
  popover: {
    marginTop: theme.spacing(0.5),
  },
  popoverPaper: {
    width: 320,
    maxHeight: 400,
    display: "flex",
    flexDirection: "column",
  },
  searchBox: {
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  searchInputWrapper: {
    flex: 1,
  },
  visibilityToggle: {
    padding: 6,
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  visibilityToggleActive: {
    color: (props) => props.clientColor || theme.palette.primary.main,
  },
  searchInput: {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.divider,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.text.secondary,
      },
      "&.Mui-focused fieldset": {
        borderColor: (props) => props.clientColor || theme.palette.primary.main,
      },
    },
  },
  listContainer: {
    flex: 1,
    overflow: "auto",
  },
  folderHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.75, 1),
    cursor: "pointer",
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    transition: "all 150ms linear",
    "&:hover": {
      backgroundColor: (props) =>
        `${props.clientColor || theme.palette.primary.main}15`,
    },
  },
  folderExpandIcon: {
    fontSize: "1.1rem",
    color: theme.palette.text.secondary,
    transition: "transform 0.2s",
  },
  folderExpandIconOpen: {
    transform: "rotate(90deg)",
  },
  folderIcon: {
    fontSize: "1.1rem",
  },
  folderName: {
    flex: 1,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  folderCount: {
    fontSize: "0.7rem",
    color: theme.palette.text.disabled,
    marginLeft: theme.spacing(1),
  },
  listItem: {
    paddingLeft: theme.spacing(4),
    fontSize: "13px",
    padding: "0 30px 0 32px",
    margin: "0 3px",
    borderRadius: "2px",
    transition: "all 150ms linear",
    lineHeight: "2",
    "&:hover": {
      backgroundColor: (props) =>
        props.clientColor || theme.palette.primary.main,
      opacity: 0.8,
      color: "#fff",
      "& $scenarioIcon": {
        color: "#fff",
      },
      "& $scenarioName span": {
        color: "#fff",
      },
    },
  },
  listItemSelected: {
    backgroundColor: (props) =>
      `${props.clientColor || theme.palette.primary.main} !important`,
    color: "#fff !important",
    "& $scenarioIcon": {
      color: "#fff",
    },
    "& $scenarioName span": {
      color: "#fff",
    },
    "&:hover": {
      backgroundColor: (props) =>
        `${props.clientColor || theme.palette.primary.main} !important`,
      opacity: 0.9,
    },
  },
  scenarioIcon: {
    minWidth: 32,
    color: theme.palette.text.secondary,
  },
  scenarioName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  noResults: {
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  emptyState: {
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
}));

const ScenarioSelector = ({
  scenarios,
  folders = [],
  value,
  onChange,
  placeholder,
  disabled = false,
  clientColor,
  fullWidth = false,
}) => {
  const classes = useStyles({ clientColor });
  const { t } = useTranslation("translation", {
    keyPrefix: "rgm.cockpit.scenario_manager.scenario_selector",
  });
  const displayPlaceholder = placeholder || t("placeholder");
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedFolders, setExpandedFolders] = useState({});
  const [visibilityFilter, setVisibilityFilter] = useState("all"); // "all", "shared", "private"
  const anchorRef = useRef(null);
  const searchInputRef = useRef(null);
  const popoverActions = useRef(null);

  // Focus search input when popover opens
  useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [open]);

  // Reposition popover when folders expand/collapse
  useEffect(() => {
    if (open && popoverActions.current) {
      setTimeout(() => {
        popoverActions.current.updatePosition();
      }, 50);
    }
  }, [expandedFolders, open]);

  // Reset search and expanded state when closing
  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setExpandedFolders({});
    }
  }, [open]);

  const toggleFolder = (folderId) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [folderId]: prev[folderId] === undefined ? false : !prev[folderId],
    }));
  };

  // When searching, force all folders open; default to expanded
  const isFolderExpanded = (folderId) => {
    if (searchTerm) return true; // Always expanded when searching
    return expandedFolders[folderId] !== false; // Default to expanded (true)
  };

  // Get selected scenario object
  const selectedScenario = useMemo(() => {
    if (!value) return null;
    return scenarios.find((s) => s.scenario_id === value);
  }, [scenarios, value]);

  // Group scenarios by folder with search filtering
  const groupedScenarios = useMemo(() => {
    const searchLower = searchTerm.toLowerCase().trim();

    // Filter scenarios by search term
    // Filter by search term and visibility
    const filteredScenarios = scenarios.filter((s) => {
      // Search filter
      if (searchLower && !s.scenario_name.toLowerCase().includes(searchLower)) {
        return false;
      }
      // Visibility filter
      if (visibilityFilter === "shared" && s.visibility === "private") {
        return false;
      }
      if (visibilityFilter === "private" && s.visibility !== "private") {
        return false;
      }
      return true;
    });

    // Build folder lookup map
    const folderMap = {};
    const buildFolderMap = (folderList, parentPath = "") => {
      folderList.forEach((folder) => {
        const path = parentPath
          ? `${parentPath} / ${folder.name}`
          : folder.name;
        folderMap[folder.folder_id] = {
          ...folder,
          displayPath: path,
        };
        if (folder.subfolders?.length > 0) {
          buildFolderMap(folder.subfolders, path);
        }
      });
    };
    buildFolderMap(folders);

    // Group scenarios
    const groups = {};
    const ungrouped = [];

    filteredScenarios.forEach((scenario) => {
      if (scenario.is_archived) return; // Don't show archived in selector

      if (scenario.folder_id && folderMap[scenario.folder_id]) {
        const folder = folderMap[scenario.folder_id];
        if (!groups[scenario.folder_id]) {
          groups[scenario.folder_id] = {
            folder,
            scenarios: [],
          };
        }
        groups[scenario.folder_id].scenarios.push(scenario);
      } else {
        ungrouped.push(scenario);
      }
    });

    // Sort groups by folder display order/name
    const sortedGroups = Object.values(groups).sort((a, b) => {
      if (a.folder.display_order !== b.folder.display_order) {
        return a.folder.display_order - b.folder.display_order;
      }
      return a.folder.displayPath.localeCompare(b.folder.displayPath);
    });

    // Sort scenarios within each group by name
    sortedGroups.forEach((group) => {
      group.scenarios.sort((a, b) =>
        a.scenario_name.localeCompare(b.scenario_name),
      );
    });
    ungrouped.sort((a, b) => a.scenario_name.localeCompare(b.scenario_name));

    return { groups: sortedGroups, ungrouped };
  }, [scenarios, folders, searchTerm, visibilityFilter]);

  const handleOpen = () => {
    if (!disabled) {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSelect = (scenarioId) => {
    onChange(scenarioId);
    handleClose();
  };

  const totalCount =
    groupedScenarios.groups.reduce((sum, g) => sum + g.scenarios.length, 0) +
    groupedScenarios.ungrouped.length;

  return (
    <Box
      className={classes.selectorRoot}
      style={fullWidth ? { width: "100%" } : {}}
    >
      {/* Selector Button */}
      <Box
        ref={anchorRef}
        className={`${classes.selectorButton} ${disabled ? classes.selectorButtonDisabled : ""} ${open ? classes.selectorButtonFocused : ""}`}
        onClick={handleOpen}
        style={fullWidth ? { width: "100%" } : {}}
      >
        {selectedScenario ? (
          <Typography className={classes.selectedText}>
            {selectedScenario.scenario_name}
          </Typography>
        ) : (
          <Typography className={classes.placeholder}>
            {displayPlaceholder}
          </Typography>
        )}
        <ExpandMoreIcon
          className={`${classes.expandIcon} ${open ? classes.expandIconOpen : ""}`}
          fontSize="small"
        />
      </Box>

      {/* Dropdown Popover */}
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        action={popoverActions}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        className={classes.popover}
        disableScrollLock
        getContentAnchorEl={null}
        PaperProps={{
          className: classes.popoverPaper,
        }}
      >
        {/* Search Input and Visibility Toggle */}
        <Box className={classes.searchBox}>
          <Box className={classes.searchInputWrapper}>
            <TextField
              inputRef={searchInputRef}
              fullWidth
              size="small"
              variant="outlined"
              placeholder={t("search_placeholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={classes.searchInput}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Tooltip
            title={
              visibilityFilter === "all"
                ? t("visibility.show_all")
                : visibilityFilter === "shared"
                  ? t("visibility.shared_only")
                  : t("visibility.private_only")
            }
          >
            <IconButton
              size="small"
              className={`${classes.visibilityToggle} ${visibilityFilter !== "all" ? classes.visibilityToggleActive : ""}`}
              onClick={() => {
                // Cycle: all -> shared -> private -> all
                setVisibilityFilter((prev) =>
                  prev === "all"
                    ? "shared"
                    : prev === "shared"
                      ? "private"
                      : "all",
                );
              }}
            >
              {visibilityFilter === "private" ? (
                <PrivateIcon fontSize="small" />
              ) : (
                <SharedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Scenario List */}
        <Box className={classes.listContainer}>
          {scenarios.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography variant="body2">{t("no_scenarios")}</Typography>
            </Box>
          ) : totalCount === 0 ? (
            <Box className={classes.noResults}>
              <Typography variant="body2">
                {t("no_results")} &quot;{searchTerm}&quot;
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {/* Grouped Scenarios */}
              {groupedScenarios.groups.map((group) => {
                const isExpanded = isFolderExpanded(group.folder.folder_id);
                return (
                  <React.Fragment key={group.folder.folder_id}>
                    <Box
                      className={classes.folderHeader}
                      onClick={() => toggleFolder(group.folder.folder_id)}
                    >
                      <ChevronRightIcon
                        className={`${classes.folderExpandIcon} ${isExpanded ? classes.folderExpandIconOpen : ""}`}
                      />
                      {isExpanded ? (
                        <FolderOpenIcon
                          className={classes.folderIcon}
                          style={{ color: group.folder.color || "#6B7280" }}
                        />
                      ) : (
                        <FolderIcon
                          className={classes.folderIcon}
                          style={{ color: group.folder.color || "#6B7280" }}
                        />
                      )}
                      <Typography className={classes.folderName}>
                        {group.folder.displayPath}
                      </Typography>
                      <Typography className={classes.folderCount}>
                        ({group.scenarios.length})
                      </Typography>
                    </Box>
                    <Collapse in={isExpanded}>
                      {group.scenarios.map((scenario) => (
                        <ListItem
                          key={scenario.scenario_id}
                          button
                          className={`${classes.listItem} ${
                            value === scenario.scenario_id
                              ? classes.listItemSelected
                              : ""
                          }`}
                          onClick={() => handleSelect(scenario.scenario_id)}
                        >
                          <ListItemIcon className={classes.scenarioIcon}>
                            <ScenarioIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={scenario.scenario_name}
                            primaryTypographyProps={{
                              className: classes.scenarioName,
                              variant: "body2",
                            }}
                          />
                        </ListItem>
                      ))}
                    </Collapse>
                  </React.Fragment>
                );
              })}

              {/* Ungrouped Scenarios */}
              {groupedScenarios.ungrouped.length > 0 && (
                <React.Fragment>
                  <Box
                    className={classes.folderHeader}
                    onClick={() => toggleFolder("ungrouped")}
                  >
                    <ChevronRightIcon
                      className={`${classes.folderExpandIcon} ${isFolderExpanded("ungrouped") ? classes.folderExpandIconOpen : ""}`}
                    />
                    <UngroupedIcon className={classes.folderIcon} />
                    <Typography className={classes.folderName}>
                      {t("ungrouped")}
                    </Typography>
                    <Typography className={classes.folderCount}>
                      ({groupedScenarios.ungrouped.length})
                    </Typography>
                  </Box>
                  <Collapse in={isFolderExpanded("ungrouped")}>
                    {groupedScenarios.ungrouped.map((scenario) => (
                      <ListItem
                        key={scenario.scenario_id}
                        button
                        className={`${classes.listItem} ${
                          value === scenario.scenario_id
                            ? classes.listItemSelected
                            : ""
                        }`}
                        onClick={() => handleSelect(scenario.scenario_id)}
                      >
                        <ListItemIcon className={classes.scenarioIcon}>
                          <ScenarioIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={scenario.scenario_name}
                          primaryTypographyProps={{
                            className: classes.scenarioName,
                            variant: "body2",
                          }}
                        />
                      </ListItem>
                    ))}
                  </Collapse>
                </React.Fragment>
              )}
            </List>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

ScenarioSelector.propTypes = {
  scenarios: PropTypes.array.isRequired,
  folders: PropTypes.array,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  clientColor: PropTypes.string,
  fullWidth: PropTypes.bool,
};

export default ScenarioSelector;
